import React, { useEffect, useState } from "react";
import httpService from "../../../services/http/settings/app-settings.service";
import UnlimitInput from "../../../components/ui-kit/unlimit-input";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import notificationService from "../../../services/toaster.service";
import { hydrateErrors } from "../../../utils/error.utils";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import UnlimitSubmitButton from "../../../components/ui-kit/unlimit-submit-button";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";

const AppSettingForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [formFields, setFormFields] = useState({});
  const { register, handleSubmit, formState, setValue, setError } = useForm();
  const { errors } = formState;
  const { hasPermission } = useRoleAndPerm();

  const hydratePredefinedValues = (data) => {
    try {
      setFormFields(data);
      for (const key in data) {
        setValue(key, data[key] || "");
      }
    } catch (error) {
      console.error("Error hydrating values:", error);
      setFormFields({});
    }
  };

  const getAppSetting = async () => {
    try {
      setPageLoading(true);
      await httpService
        .getAppSetting()
        .then((response) => {
          if (response && response.status === 200) {
            const data = response.data.data;
            hydratePredefinedValues(data);
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    } finally {
      setPageLoading(false);
    }
  };

  const fetchPredefinedValues = async () => {
    await getAppSetting();
  };

  const onSubmitForm = async (data) => {
    try {
      setIsLoading(true);

      const newFormDataObject = new FormData();
      for (const key in data) {
        newFormDataObject.append(key, data[key]);
      }

      await updateAppSetting(newFormDataObject);
    } finally {
      setIsLoading(false);
    }
  };

  const updateAppSetting = async (formData) => {
    await httpService
      .updateAppSetting(formData, true)
      .then((response) => {
        if (response.status === 200) {
          notificationService.successMessage(
            t("appSetting.appSettingUpdateSuccess")
          );
          navigate(all_routes.updateAppSettings);
        }
      })
      .catch((error) => {
        if (error.validation_error?.errors) {
          hydrateErrors(error.validation_error.errors, setError);
        }
      });
  };

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    {
      label: t("appSetting.editAppSetting"),
      active: true,
    },
  ];

  const getInputType = (key, value) => {
    if (key.includes("url") || key.includes("_url")) {
      return "url";
    }
    if (typeof value === "number" || key.includes("number")) {
      return "number";
    }
    return "text";
  };

  const getValidationRules = (key, value) => {
    const rules = {
      required: t("inputField.validation.required"),
    };

    if (key.includes("url") || key.includes("_url")) {
      rules.pattern = {
        value:
          /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w._~:/?#[\]@!$&'()*+,;=-]*)?$/,
        message: t("inputField.validation.invalidURL"),
      };
    }

    if (typeof value === "number" || key.includes("number")) {
      rules.min = {
        value: 0,
        message: t("inputField.validation.minValue", { min: 0 }),
      };
    }

    return rules;
  };

  const getFieldLabel = (key) => {
    const labelMap = {
      contact_us: t("inputField.label.contactUs"),
      gdpr_url: t("inputField.placeholder.enterGDPRUrl"),
      terms_and_conditions_url: t("inputField.label.termsAndConditionsUrl"),
      privacy_policy_url: t("inputField.label.privacyPolicyUrl"),
      number_of_persons_allow_in_shop: t(
        "inputField.label.number_of_persons_allow_in_shop"
      ),
      about_lemvig_muller: t("inputField.label.enterAboutLemvigMuller"),
    };

    return (
      labelMap[key] ||
      key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  useEffect(() => {
    fetchPredefinedValues();
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex flex-column">
            <div className="page-title">
              <h4>{t("appSetting.editAppSetting")}</h4>
            </div>
            <UnlimitBreadcrumb items={breadcrumbItems} />
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <div className="card">
            <div className="card-body pb-0">
              {pageLoading ? (
                <div className="row d-flex justify-content-center">
                  <UnlimitLoading width={50} height={50} />
                </div>
              ) : (
                <div>
                  <div className="row mb-4">
                    <div className="col-md-12">
                      <h4 className="mb-0">
                        {t("appSetting.addNewAppSetting")}
                      </h4>
                    </div>
                  </div>

                  <div className="row">
                    {Object.entries(formFields).map(([key, value], index) => (
                      <div className="col-md-6" key={key}>
                        <UnlimitInput
                          label={getFieldLabel(key)}
                          id={key}
                          type={getInputType(key, value)}
                          register={register}
                          rules={getValidationRules(key, value)}
                          error={errors[key]}
                          errorMessage={errors[key]?.message}
                        />
                      </div>
                    ))}
                  </div>

                  {hasPermission(["setting-edit"]) && (
                    <div className="col-lg-12 mb-4 mt-4">
                      <UnlimitSubmitButton
                        label={t("general.button.update")}
                        isLoading={isLoading}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppSettingForm;
